import operationTypes from 'constants/operationTypes';
import transactionTypes from 'constants/transactionTypes';
import { Operation, Society, Transaction } from 'types';

import { getHoldingClassFromTransaction } from './getHoldingClassFromTransaction';
import { getTransactionNominalValue } from './getTransactionNominalValue';
import { getTransactionSharesCount } from './getTransactionSharesCount';
import { getOperationNominalValue } from './getOperationNominalValue';

const isBuyTransaction = (transaction: Transaction) =>
  transaction.transactionType === transactionTypes.BUY;

const isCapitalDecreaseTransaction = (transaction: Transaction) =>
  transaction.transactionType === transactionTypes.CAPITAL_DECREASE;

const isCapitalDecreaseWithSharePrice = (transaction: Transaction) =>
  isCapitalDecreaseTransaction(transaction) && transaction?.sharePrice;

const filterTransactions = (transactions: Transaction[]): Transaction[] =>
  transactions?.filter(
    (transaction) =>
      (transaction as Transaction).transactionType !==
        transactionTypes.ADD_PARTNER &&
      (transaction as Transaction).transactionType !== transactionTypes.SELL
  );

const getOtherOperationsAmount = (operation: Operation, society: Society) => {
  const transactions = filterTransactions(
    operation?.transactions as Transaction[]
  );

  if (!transactions.length) return null;

  const amount = transactions.reduce((acc, transaction) => {
    let res = acc;
    const shareCount = getTransactionSharesCount(transaction);
    const nominalValue = getTransactionNominalValue({
      holdingClass: getHoldingClassFromTransaction(transaction),
      transaction,
      operation,
      society,
    });

    if (
      isBuyTransaction(transaction) ||
      isCapitalDecreaseWithSharePrice(transaction)
    ) {
      res += shareCount * (transaction?.sharePrice || 0);
    } else {
      res += (nominalValue + (transaction?.sharePremium || 0)) * shareCount;
    }
    return res;
  }, 0);

  return amount;
};

/**
 * Calculates the total amount of an operation based on its type and transactions.
 *
 * @param {Object} operation - The operation object containing details about the operation.
 * @param {Object} society - The society object containing details about the society.
 * @returns {number|null} - The total amount of the operation or null if the operation is invalid or has no relevant transactions.
 */
export const getOperationAmount = (operation: Operation, society: Society) => {
  if (!operation) return null;

  switch (operation.operationType) {
    case operationTypes.CONTRIBUTION: {
      return operation.contribution?.totalAmount;
    }
    case operationTypes.DIVIDENDS_DISTRIBUTION: {
      return operation.dividend?.totalAmount;
    }
    case operationTypes.RECLASSIFICATION:
    case operationTypes.SPLIT: {
      return 0;
    }

    default: {
      const amount = getOtherOperationsAmount(operation, society);
      return amount;
    }
  }
};
